import React, { useId, useState } from 'react';
import { Property } from '../../services/api';

interface MapSearchProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  setSearchFocused: (value: boolean) => void;
  searchSuggestions: Property[];
  showSearchDropdown: boolean;
  handleSuggestionSelect: (property: Property) => void;
}

/** Shared by the mobile app bar and desktop panel; mouse, touch and keyboard agree. */
export const MapSearch: React.FC<MapSearchProps> = ({
  searchQuery, setSearchQuery, setSearchFocused, searchSuggestions,
  showSearchDropdown, handleSuggestionSelect,
}) => {
  const id = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  const open = showSearchDropdown && searchSuggestions.length > 0;
  const selected = activeIndex < searchSuggestions.length ? activeIndex : -1;

  return (
    <div className="relative mx-auto w-full max-w-[480px]" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setSearchFocused(false);
        setActiveIndex(-1);
      }
    }}>
      <div className="map-search-field flex h-11 items-center gap-1 rounded-ca-xl bg-frame pl-2.5">
        <svg width={16} height={16} className="shrink-0 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="search"
          role="combobox"
          aria-label="단지 검색"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? `${id}-results` : undefined}
          aria-activedescendant={open && selected >= 0 ? `${id}-${selected}` : undefined}
          autoComplete="off"
          value={searchQuery}
          onChange={(event) => { setSearchQuery(event.target.value); setActiveIndex(-1); setSearchFocused(true); }}
          onFocus={() => setSearchFocused(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setSearchFocused(false);
              setActiveIndex(-1);
              return;
            }
            if (!open) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const next = event.key === 'ArrowDown'
                ? (selected + 1) % searchSuggestions.length
                : (selected <= 0 ? searchSuggestions.length - 1 : selected - 1);
              setActiveIndex(next);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              handleSuggestionSelect(searchSuggestions[selected >= 0 ? selected : 0]);
              setActiveIndex(-1);
            }
          }}
          placeholder="단지·지역 검색"
          className="min-w-0 flex-1 bg-transparent py-2 text-[14px] font-medium text-ink outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden"
        />
        {searchQuery ? (
          <button type="button" aria-label="검색어 지우기"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { setSearchQuery(''); setActiveIndex(-1); }}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-ca-lg text-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink">
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        ) : <span className="w-2" />}
      </div>
      {open && (
        <div id={`${id}-results`} role="listbox" aria-label="단지 검색 결과" className="absolute inset-x-0 top-full z-30 mt-2 max-h-[60vh] overflow-y-auto rounded-ca-xl border border-ca-line bg-frame shadow-ca-sheet">
          {searchSuggestions.map((property, index) => (
            <button key={property.id} id={`${id}-${index}`} type="button" role="option" aria-selected={selected === index}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => { handleSuggestionSelect(property); setActiveIndex(-1); }}
              className={`block min-h-[56px] w-full border-b border-ca-line px-3 py-2.5 text-left last:border-0 hover:bg-ca-surface focus-visible:bg-ca-surface ${selected === index ? 'bg-ca-surface' : ''}`}>
              <span className="block truncate text-[14px] font-bold text-ink">{property.title}{property.status === '분양완료' && <span className="ml-1.5 text-[12px] font-medium text-muted">분양완료</span>}</span>
              <span className="mt-0.5 block truncate text-[13px] text-muted">{property.address}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
